import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import MessageBubble from './MessageBubble';
import DisclaimerBanner from './DisclaimerBanner';
import ChatInputArea from './ChatInputArea';
import FeedbackForm from './FeedbackForm';
import ConfirmationModal from '../ConfirmationPopUp';
import CloseIcon from './icons/CloseIcon';
import CheckmarkIcon from './icons/CheckmarkIcon';
import { getMinuteKey } from './utils/formatTimestamp';

const ChatPopover = ({
  isOpen,
  enabled,
  botName,
  botTitle,
  messages,
  inputValue,
  isLoading,
  isActiveConversationLoading,
  isRecording,
  isVoiceSupported,
  browserSupportMessage,
  ticketSubmissionState,
  ticketSubmissionMessage,
  feedbackModalOpen,
  feedbackMessageId,
  feedbackErrorMessage,
  isSubmittingFeedback,
  ticketConfirmationModalOpen,
  onClose,
  onInputChange,
  onSend,
  onMicPress,
  onRetry,
  onFeedback,
  onFeedbackCancel,
  onFeedbackSubmit,
  onSubmitTicket,
  onEndChat,
  onConfirmTicketSubmission,
  onCancelTicketSubmission,
  messageListRef,
  popoverRef
}) => {
  const minuteKeys = useMemo(
    () => messages.map((message) => getMinuteKey(message.createdAt)),
    [messages]
  );

  const interactionDisabled = !enabled || isLoading || isActiveConversationLoading;
  const isSubmittingTicket = ticketSubmissionState === 'submitting';
  const isEndingChat = ticketSubmissionState === 'ending';

  if (!isOpen) {
    return null;
  }

  return (
    <div className="chatbot-popover" role="dialog" aria-label="AI assistant chat window" ref={popoverRef}>
      <div className="chatbot-header ai-mode">
        <div>
          <h2>{botName}</h2>
          <div className="chatbot-subtitle">
            <span>{botTitle}</span>
            <span className="chatbot-online-dot" />
            <span>Online</span>
          </div>
        </div>
        <button type="button" className="chatbot-close-btn" onClick={onClose} aria-label="Close chat">
          <CloseIcon />
        </button>
      </div>
      <div className="chatbot-body">
        {!enabled ? (
          <div className="chatbot-disabled-state" aria-live="polite">
            Chat is currently unavailable.
          </div>
        ) : (
          <>
            <div className="chatbot-message-list" aria-live="polite" ref={messageListRef}>
              {isActiveConversationLoading ? (
                <div className="chatbot-conversation-skeleton" aria-hidden="true">
                  <div className="chatbot-conversation-skeleton-row">
                    <Skeleton circle width={32} height={32} />
                    <Skeleton width="72%" height={38} borderRadius={12} />
                  </div>
                  <div className="chatbot-conversation-skeleton-row chatbot-conversation-skeleton-row-user">
                    <Skeleton width="66%" height={38} borderRadius={12} />
                  </div>
                  <div className="chatbot-conversation-skeleton-row">
                    <Skeleton circle width={32} height={32} />
                    <Skeleton width="54%" height={38} borderRadius={12} />
                  </div>
                </div>
              ) : (
                messages.map((message, index) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    showTimestamp={index === 0 || minuteKeys[index - 1] !== minuteKeys[index]}
                    onRetry={onRetry}
                    onFeedback={onFeedback}
                    isStreaming={message.role === 'bot' && message.status === 'loading'}
                    streamingDelay={message.streamingDelay || 50}
                  />
                ))
              )}
            </div>
            <div className="chatbot-footer">
              <DisclaimerBanner />
              {browserSupportMessage && <div className="chatbot-browser-note" role="note">{browserSupportMessage}</div>}
              {ticketSubmissionState === 'success' && (
                <div className="chatbot-ticket-status chatbot-ticket-status-success" role="status" aria-live="polite">
                  <span className="chatbot-ticket-status-icon" aria-hidden="true">
                    <CheckmarkIcon />
                  </span>
                  <div className="chatbot-ticket-status-content">
                    <strong>Support ticket submitted</strong>
                    <span>{ticketSubmissionMessage || 'Our team will review your transcript and follow up soon.'}</span>
                  </div>
                </div>
              )}
              {ticketSubmissionState === 'error' && (
                <div className="chatbot-ticket-status chatbot-ticket-status-error" role="alert">
                  <div className="chatbot-ticket-status-content">
                    <strong>Ticket submission failed</strong>
                    <span>{ticketSubmissionMessage || 'Could not submit ticket. Retry?'}</span>
                  </div>
                  <button type="button" onClick={onSubmitTicket} aria-label="Retry support ticket submission" disabled={interactionDisabled}>
                    Retry
                  </button>
                </div>
              )}
              <div className="chatbot-action-buttons">
                <button
                  type="button"
                  className="chatbot-ticket-button"
                  onClick={onSubmitTicket}
                  aria-label="Submit support ticket"
                  disabled={interactionDisabled || isSubmittingTicket}
                >
                  {isSubmittingTicket ? 'Submitting...' : 'Submit Ticket'}
                </button>
                <button
                  type="button"
                  className="chatbot-ticket-button"
                  onClick={onEndChat}
                  aria-label="End chat"
                  disabled={interactionDisabled || isEndingChat}
                >
                  {isEndingChat ? 'Ending...' : 'End Chat'}
                </button>
              </div>
              <ChatInputArea
                value={inputValue}
                onChange={onInputChange}
                onSend={onSend}
                isDisabled={interactionDisabled}
                isRecording={isRecording}
                isVoiceSupported={isVoiceSupported}
                onMicPress={onMicPress}
              />
            </div>
          </>
        )}
      </div>
      <FeedbackForm
        isOpen={feedbackModalOpen}
        messageId={feedbackMessageId}
        onClose={onFeedbackCancel}
        onSubmit={onFeedbackSubmit}
        isLoading={isSubmittingFeedback}
        errorMessage={feedbackErrorMessage}
      />
      <ConfirmationModal
        isOpen={ticketConfirmationModalOpen}
        onClose={onCancelTicketSubmission}
        onSave={onConfirmTicketSubmission}
        buttonPosition="center"
        confirmButton="Yes"
        cancelButton="Cancel"
        titleName="Resubmit support request"
        content={'A support request for this conversation was already submitted.<br /> <strong>Do you want to submit again?</strong>'}
        isRawHtmlContent={true}
        confirmationInProgress={isLoading}
        isLoading={isLoading}
      />
    </div>
  );
};

ChatPopover.propTypes = {
  isOpen: PropTypes.bool,
  enabled: PropTypes.bool,
  botName: PropTypes.string.isRequired,
  botTitle: PropTypes.string.isRequired,
  messages: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    text: PropTypes.string,
    createdAt: PropTypes.string.isRequired,
    streamingDelay: PropTypes.number
  })),
  inputValue: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  isActiveConversationLoading: PropTypes.bool,
  isRecording: PropTypes.bool,
  isVoiceSupported: PropTypes.bool,
  browserSupportMessage: PropTypes.string,
  ticketSubmissionState: PropTypes.oneOf(['idle', 'submitting', 'success', 'error', 'ending']),
  ticketSubmissionMessage: PropTypes.string,
  feedbackModalOpen: PropTypes.bool,
  feedbackMessageId: PropTypes.string,
  feedbackErrorMessage: PropTypes.string,
  isSubmittingFeedback: PropTypes.bool,
  ticketConfirmationModalOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  onMicPress: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  onFeedback: PropTypes.func.isRequired,
  onFeedbackCancel: PropTypes.func.isRequired,
  onFeedbackSubmit: PropTypes.func.isRequired,
  onSubmitTicket: PropTypes.func.isRequired,
  onEndChat: PropTypes.func.isRequired,
  onConfirmTicketSubmission: PropTypes.func.isRequired,
  onCancelTicketSubmission: PropTypes.func.isRequired,
  messageListRef: PropTypes.shape({
    current: PropTypes.any
  }),
  popoverRef: PropTypes.shape({ current: PropTypes.any })
};

ChatPopover.defaultProps = {
  isOpen: false,
  enabled: true,
  messages: [],
  isLoading: false,
  isActiveConversationLoading: false,
  isRecording: false,
  isVoiceSupported: false,
  browserSupportMessage: '',
  ticketSubmissionState: 'idle',
  ticketSubmissionMessage: '',
  feedbackModalOpen: false,
  feedbackMessageId: '',
  feedbackErrorMessage: '',
  isSubmittingFeedback: false,
  ticketConfirmationModalOpen: false,
  messageListRef: null,
  popoverRef: null
};

export default ChatPopover;
